import {
  Checkbox,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  MaskedInput,
  Paragraph,
  Reservation,
  ReservationCard,
  Select,
  SubmitButton,
  TextInput,
} from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import config from 'config';
import { ContactRelationship } from 'domains/client';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import {
  getRelationshipOptions,
  INSURANCE_OPTION,
  OTHER_INSURANCE_OPTION,
  TherapyTargetValue,
  therapyTargetValues,
} from 'domains/signupDirect/misc/constants';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { ConsultationFormType, ConsultationForm } from 'domains/signupDirect/schemas/consultationSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { get, noop } from 'lodash';
import { DeepPartial, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getReservationScheduleProps } from 'utils/date';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { useTrack } from 'utils/hooks/useMixPanel';
import { ScheduleSection } from './Schedule/ScheduleSection';
import { submitConsultationForm } from './submitConsultationForm';
import { usePeriodicConsultationFormSave } from 'domains/signupDirect/misc/localStorage';
import { DirectSignupInsuranceConsultationEligibilitySection } from '../Evaluation/Insurance/Medicaid/InsuranceConsultationCardSection';
import { InsurancePayment } from 'domains/signupDirect/schemas/filtersSchema';
import { getInsuranceSchemaType } from 'domains/signupDirect/schemas/evaluationSchema';
import { isPCPRequired } from '../businessLogic/isPCPRequired';

const getDefaultRelationshipToClient = (
  target: TherapyTargetValue,
): ConsultationFormType['contactInfo']['relationshipToClient'] | undefined => {
  switch (target) {
    case 'adult':
      return ContactRelationship.Client;
    default:
      return undefined;
  }
};

const Component = () => {
  const { filters, incompleteEvalForm, incompleteConsultForm, isResumedLead } = useSignupDirectCtx();
  const incompleteForm = incompleteConsultForm ?? incompleteEvalForm;

  const { t } = useTranslation();
  useAutoScrollToTop();

  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.CONSULT.FORM, { isResumedLead });

  if (!filters) throw 'Expected filters to be set';

  const methods = useForm<ConsultationFormType>({
    resolver: zodResolver(ConsultationForm),
    defaultValues: {
      concerns: {
        clinicalAreas: filters.clinicalAreas,
        clinicalConcernKeys: filters.clinicalConcernKeys,
      },
      clientType: filters.therapyTarget,
      clientInfo: {
        firstName: incompleteForm?.clientInfo?.firstName,
        lastName: incompleteForm?.clientInfo?.lastName,
        dateOfBirth: incompleteForm?.clientInfo?.dateOfBirth,
        language: filters.language,
        stateCode: filters.stateCode,
      },
      contactInfo: {
        relationshipToClient:
          incompleteForm?.contactInfo?.relationshipToClient ?? getDefaultRelationshipToClient(filters.therapyTarget),
        email: incompleteForm?.contactInfo?.email,
        firstName: incompleteForm?.contactInfo?.firstName,
        lastName: incompleteForm?.contactInfo?.lastName,
        phone: incompleteForm?.contactInfo?.phone,
      },
      primaryPhysician: {
        ...incompleteEvalForm?.primaryPhysician,
        physicianOrderRequired: isPCPRequired(filters.paymentMethod),
      },
      billingInfo:
        filters.paymentMethod.type === 'insurance'
          ? {
              type: getInsuranceSchemaType(filters.paymentMethod.insuranceData.insuranceType),
              insuranceData: (filters.paymentMethod as InsurancePayment).insuranceData,

              subscriber:
                incompleteEvalForm?.billingInfo?.type === 'insurance-commercial'
                  ? incompleteEvalForm?.billingInfo.subscriber
                  : undefined,
              memberId: get(incompleteEvalForm, 'billingInfo.memberId'),
            }
          : filters.paymentMethod.type === 'privatePay'
          ? {
              type: 'privatePay',
            }
          : {
              type: 'no-insurance',
            },
    },
  });

  const { handleSubmit } = methods;

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.sections.consultation.title')}</Heading>
        <Paragraph size="lg" noDefaultMargins>
          {t('directSignup.sections.consultation.subtitle')}
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout className="mobile-xs-d:gap-e40 tablet-d:gap-e56" onSubmit={handleSubmit(noop)}>
          <FormFields />
          <SubmitSection />
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { register, watch } = useFormContext<ConsultationFormType>();
  const { filters } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';

  const relationshipOptions = getRelationshipOptions(t);

  useSignupNavigationGuard();
  usePeriodicConsultationFormSave();

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <ScheduleSection />

        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.clientInfo.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.clientInfo.subTitle')}
          </Paragraph>
        </HeadingCombo>
        <GridFieldGroup>
          <TextInput
            data-testid="client-first-name-input"
            label={t('directSignup.labels.clientFirstName')}
            {...register('clientInfo.firstName')}
          />
          <TextInput
            data-testid="client-last-name-input"
            label={t('directSignup.labels.clientLastName')}
            {...register('clientInfo.lastName')}
          />
          <MaskedInput
            hint={
              filters.paymentMethod.type === INSURANCE_OPTION || filters.paymentMethod.type === OTHER_INSURANCE_OPTION
                ? t('directSignup.labels.dob.insurance.hint')
                : filters.therapyTarget === therapyTargetValues[0]
                ? t('directSignup.labels.dob.privatePay.child.hint')
                : t('directSignup.labels.dob.privatePay.adult.hint')
            }
            data-testid="client-dob-input"
            label={t('directSignup.labels.dob')}
            {...register('clientInfo.dateOfBirth')}
            mask="MM/DD/YYYY"
            showMask
            type="tel"
            replacement={{
              M: /[0-1]|[0-9]/,
              D: /[0-3]|[0-9]/,
              Y: /\d/,
            }}
          />
        </GridFieldGroup>
      </FormLayout.Fields>

      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.contactInfo.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.contactInfo.subTitle')}
          </Paragraph>
        </HeadingCombo>
        <Select
          data-testid="contact-relationship-to-client-select"
          label={t('directSignup.labels.relationshipToClient')}
          name="contactInfo.relationshipToClient"
          options={relationshipOptions}
          disabled={filters?.therapyTarget === 'adult'}
        />
        <GridFieldGroup>
          {watch('contactInfo.relationshipToClient') !== ContactRelationship.Client && (
            <>
              <TextInput
                data-testid="contact-first-name-input"
                label={t('directSignup.labels.contactFirstName')}
                {...register('contactInfo.firstName')}
              />
              <TextInput
                data-testid="contact-last-name-input"
                label={t('directSignup.labels.contactLastName')}
                {...register('contactInfo.lastName')}
              />
            </>
          )}

          <TextInput
            data-testid="contact-email-input"
            label={t('directSignup.labels.email')}
            type="email"
            {...register('contactInfo.email')}
          />
          <TextInput
            data-testid="contact-phone-input"
            label={t('directSignup.labels.phone')}
            type="tel"
            {...register('contactInfo.phone')}
          />
        </GridFieldGroup>
      </FormLayout.Fields>

      {filters.paymentMethod.type !== 'privatePay' && <DirectSignupInsuranceConsultationEligibilitySection />}
    </>
  );
};

const SubmitSection = () => {
  const { register, handleSubmit, setError } = useFormContext<ConsultationFormType>();
  const { format } = useDisplayTimezone();
  const { t } = useTranslation();

  const { filters, isResumedLead } = useSignupDirectCtx();

  const scheduleTimeSlot = useWatch<DeepPartial<ConsultationFormType>, 'consultSchedule'>({
    name: 'consultSchedule',
  });

  if (!filters) throw 'Expected filters to be defined';

  const termsUrl = config.get('termsUrl') as string;
  const privacyPolicyUrl = config.get('privacyPolicyUrl') as string;
  const speechTermsUrl = config.get('speechTermsUrl') as string;
  const noticePrivacyUrl = config.get('privacyUrl') as string;

  return (
    <div className="flex flex-col w-full">
      {scheduleTimeSlot?.time && (
        <ReservationCard title={t('directSignup.eval.schedule.reservation.title')}>
          <Reservation
            title="Speech Therapy Consultation (15 min)"
            {...getReservationScheduleProps({
              dateTime: scheduleTimeSlot.time,
              formatFn: format,
            })}
          />
        </ReservationCard>
      )}
      <div className="flex flex-col gap-e32 mt-24">
        <Checkbox variant="plain" id="risk-free-checkbox" {...register('consentToTerms')}>
          <Paragraph variant="secondary" size="sm" data-testid="terms-paragraph">
            {t('directSignup.eval.form.terms.1')}
            <Link to={termsUrl} external>
              {t('directSignup.eval.form.terms.2')}
            </Link>
            {t('directSignup.eval.form.terms.3')}
            <Link to={noticePrivacyUrl} external>
              {t('directSignup.eval.form.terms.4')}
            </Link>
            {t('directSignup.eval.form.terms.5')}
            <Link to={speechTermsUrl} external>
              {t('directSignup.eval.form.terms.6')}
            </Link>
            {t('directSignup.eval.form.terms.7')}
            <Link to={privacyPolicyUrl} external>
              {t('directSignup.eval.form.terms.8')}
            </Link>
            {t('directSignup.eval.form.terms.9')}
            {t('directSignup.eval.form.terms.10')}
          </Paragraph>
        </Checkbox>
        <FormLayout.CTA>
          <SubmitButton
            data-testid="confirm-reservation-submit-button"
            onClick={handleSubmit(async d => {
              if (!d.consentToTerms) {
                setError('consentToTerms', { message: t('errors.acceptTerms') });
                return;
              }
              await submitConsultationForm(d, filters, isResumedLead);
            })}
          >
            {t('consultation.schedule.confirm.submit')}
          </SubmitButton>
        </FormLayout.CTA>
      </div>
    </div>
  );
};

export const DirectSignupScheduleConsultationForm = Component;
